/**
 * ViewportGuard Component
 *
 * Enforces minimum viewport width for web portals (768px per platform guidelines).
 * Shows mobile redirect prompt for users on smaller screens.
 *
 * Per Tonnie Platform TECH Guidelines:
 * - Journey Web minimum viewport: 768px (md breakpoint)
 * - Mobile users (<768px) should see app download prompt
 * - Citizen Mobile app serves all mobile users
 */

import { Monitor, Smartphone } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { ReactNode, useEffect, useState } from 'react';

interface IViewportGuardProps {
  /** Minimum viewport width in pixels (default: 768) */
  minWidth?: number;
  /** Children to render when viewport is valid */
  children: ReactNode;
  /** Custom fallback component for invalid viewport */
  fallback?: ReactNode;
}

/**
 * Hook to check viewport width
 */
function useViewportWidth(): number {
  const [width, setWidth] = useState(() => {
    if (typeof window === 'undefined') return 1024; // SSR fallback
    return window.innerWidth;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => setWidth(window.innerWidth);

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Initial check
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
}

/**
 * Mobile Redirect Screen
 * Shown when viewport is below minimum width
 */
function MobileRedirectScreen() {
  const { t } = useTranslation();

  // App store URLs - configured via environment or defaults to placeholders
  const appStoreUrl =
    import.meta.env.VITE_CITIZEN_APP_STORE_URL ||
    'https://apps.apple.com/pt/app/tonnie-citizen/id6778956275?l=en-GB';
  const playStoreUrl =
    import.meta.env.VITE_CITIZEN_PLAY_STORE_URL ||
    'https://play.google.com/store/apps/details?id=ai.tonnie.citizen';

  return (
    <div className="min-h-screen bg-linear-to-b from-primary-50 to-background flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary-100">
            <Smartphone className="w-10 h-10 text-primary-600" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-foreground mb-3">{t('viewport.switchToMobile')}</h1>

        {/* Description */}
        <p className="text-muted-foreground mb-8 leading-relaxed">{t('viewport.description')}</p>

        {/* App Store Buttons */}
        <div className="flex flex-col gap-3">
          <a
            href={appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full py-3 px-6 bg-foreground text-background rounded-xl hover:bg-foreground/90 transition-colors"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            <span className="font-medium">{t('viewport.downloadAppStore')}</span>
          </a>

          <a
            href={playStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full py-3 px-6 bg-foreground text-background rounded-xl hover:bg-foreground/90 transition-colors"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
            </svg>
            <span className="font-medium">{t('viewport.downloadPlayStore')}</span>
          </a>
        </div>

        {/* Desktop hint */}
        <div className="mt-8 pt-8 border-t border-border">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Monitor className="w-4 h-4" />
            <span>{t('viewport.orAccessDesktop')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * ViewportGuard - Enforces minimum viewport width
 *
 * @example
 * ```tsx
 * <ViewportGuard minWidth={768}>
 *   <App />
 * </ViewportGuard>
 * ```
 */
export function ViewportGuard({ minWidth = 768, children, fallback }: IViewportGuardProps) {
  const viewportWidth = useViewportWidth();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Small delay to prevent flash on desktop
    const timer = setTimeout(() => setIsChecking(false), 50);
    return () => clearTimeout(timer);
  }, []);

  // Don't render anything during initial check to prevent flash
  if (isChecking) {
    return null;
  }

  // Show mobile redirect for small viewports
  if (viewportWidth < minWidth) {
    return <>{fallback || <MobileRedirectScreen />}</>;
  }

  // Viewport is valid - render children
  return <>{children}</>;
}

export default ViewportGuard;
